/**
 * Copilot Provider — Core Context System
 * ═══════════════════════════════════════════════════════════════
 * Features: page context detection, multi-page memory, learning,
 * proactive warnings, anomaly detection
 */
import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useCopilotAssistMutation,
  useResolveCopilotQuery,
  useRecordCopilotActionMutation,
} from '@api/zunoStudioApi';
import CopilotOrb from './CopilotOrb';
import CopilotSidebar from './CopilotSidebar';
import CopilotToast from './CopilotToast';
import { setCopilotEmitter } from './copilotTrackingMiddleware';

const CopilotContext = createContext(null);
export const useCopilot = () => useContext(CopilotContext);

// ── 1. Page Context Auto-Detection ───────────────────────────
const PAGE_MAP = {
  '/projects':   { type: 'project', entity: 'project', label: 'Projects' },
  '/tasks':      { type: 'task', entity: 'task', label: 'Tasks' },
  '/kanban':     { type: 'task', entity: 'task', label: 'Kanban Board' },
  '/sprints':    { type: 'sprint', entity: 'sprint', label: 'Sprints' },
  '/budget':     { type: 'budget', entity: 'budget', label: 'Budget' },
  '/accounts':   { type: 'invoice', entity: 'invoice', label: 'Accounts' },
  '/milestones': { type: 'milestone', entity: 'milestone', label: 'Milestones' },
  '/goals':      { type: 'goal', entity: 'goal', label: 'Goals' },
  '/timesheets': { type: 'timesheet', entity: 'timesheet', label: 'Timesheets' },
  '/employees':  { type: 'employee', entity: 'employee', label: 'Employees' },
  '/calendar':   { type: 'calendar', entity: 'event', label: 'Calendar' },
  '/documents':  { type: 'document', entity: 'document', label: 'Documents' },
  '/reports':    { type: 'report', entity: 'report', label: 'Reports' },
  '/hivelink':   { type: 'hivelink', entity: 'message', label: 'HiveLink' },
  '/capacity':   { type: 'resource', entity: 'resource', label: 'Capacity' },
  '/studio':     { type: 'studio', entity: 'agent', label: 'Zuno Studio' },
};

function detectPageContext(pathname) {
  for (const [prefix, ctx] of Object.entries(PAGE_MAP)) {
    if (pathname.startsWith(prefix)) return { ...ctx, path: pathname };
  }
  return { type: 'dashboard', entity: 'general', label: 'Dashboard', path: pathname };
}

// ── 5. Learning Storage ──────────────────────────────────────
const LEARNING_KEY = 'copilot_learning';
function loadLearning() {
  try { return JSON.parse(localStorage.getItem(LEARNING_KEY) || '{}'); } catch { return {}; }
}
function saveLearning(data) {
  try { localStorage.setItem(LEARNING_KEY, JSON.stringify(data)); } catch { /* storage full */ }
}

export default function CopilotProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Take the user to their Copilot privacy controls.
   *
   * This previously did `window.location.href = '/settings/privacy'`, which
   * has no matching route — but `settings/:workspaceId` does match it, so the
   * click landed on the Settings page with workspaceId="privacy" and every
   * query on it failed. A broken page is worse than a 404, and a full reload
   * on top of it discarded the session state the user was mid-way through.
   *
   * An opt-out the user cannot reach is not an opt-out, and this is the
   * specific claim a compliance reviewer will click.
   */
  const openPrivacySettings = useCallback(() => {
    navigate('/settings?tab=privacy');
  }, [navigate]);
  const [copilotAssist] = useCopilotAssistMutation();
  const [enabled, setEnabled] = useState(() => localStorage.getItem('copilot_enabled') !== 'false');
  const [warnings, setWarnings] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [pageContext, setPageContext] = useState(detectPageContext(location.pathname));
  const [pageHistory, setPageHistory] = useState([]);

  /**
   * Entity context — what the page is currently *looking at*, as opposed to
   * which page it is. pageContext is derived from the pathname alone, so Zuno
   * knew it was on HiveLink but not which channel, who was in it, or what was
   * selected. A page publishes that here and it rides along on every assist
   * call.
   *
   * Cleared automatically on navigation: a stale channel name would be worse
   * than none, because the model would answer confidently about the wrong
   * thing.
   */
  const [entityContext, setEntityContext] = useState(null);

  /**
   * Keyed so several components can contribute without clobbering each other.
   * ChannelView owns 'channel'; the poll, canvas, scheduled and reminder panels
   * each own their own slice and only publish while they are mounted — which is
   * the right behaviour, since what you have open is what you are asking about.
   *
   *   publishEntity('channel', { ... })   // set or replace that slice
   *   publishEntity('polls', null)        // remove it
   *
   * Passing an object as the first argument still replaces everything, so the
   * simple single-publisher case keeps working.
   */
  const publishEntity = useCallback((keyOrEntity, value) => {
    if (keyOrEntity && typeof keyOrEntity === 'object') {
      setEntityContext(keyOrEntity);
      return;
    }
    if (typeof keyOrEntity !== 'string') { setEntityContext(null); return; }
    setEntityContext((prev) => {
      const next = { ...(prev || {}) };
      if (value === null || value === undefined) delete next[keyOrEntity];
      else next[keyOrEntity] = value;
      const result = Object.keys(next).length ? next : null;
      // Bail out when nothing actually changed. Publishers re-run whenever their
      // source data refetches, and returning a fresh object every time made the
      // provider re-render — and every consumer with it — for an identical
      // payload. Handing back `prev` makes React skip the update entirely.
      try {
        if (JSON.stringify(prev) === JSON.stringify(result)) return prev;
      } catch { /* unserialisable payload: accept the update */ }
      return result;
    });
  }, []);
  const [learning, setLearning] = useState(loadLearning);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const lastWarningCheck = useRef(0);

  // ── 1. Auto-detect page context on navigation ─────────────
  useEffect(() => {
    const newCtx = detectPageContext(location.pathname);
    setPageContext(prev => {
      // 6. Multi-page context — remember previous pages
      if (prev.path !== newCtx.path) {
        setPageHistory(h => [...h.slice(-5), { ...prev, timestamp: Date.now() }]);
      }
      return newCtx;
    });
    setWarnings([]);
    setAnomalies([]);
    // A channel name from the page you just left would make Zuno answer
    // confidently about the wrong thing.
    setEntityContext(null);
  }, [location.pathname]);

  // ── 7. Proactive warning system (check every 60s on page) ──
  useEffect(() => {
    if (!enabled) return;
    const now = Date.now();
    if (now - lastWarningCheck.current < 60000) return;
    lastWarningCheck.current = now;

    const checkWarnings = async () => {
      try {
        const res = await copilotAssist({
          action: 'detect_warnings', pageType: pageContext.type,
          entityData: { page: pageContext.label, path: pageContext.path },
        }).unwrap();
        const parsed = JSON.parse((res.data?.result || '{}').replace(/```json?\n?|```/g, ''));
        if (parsed.warnings?.length) setWarnings(parsed.warnings.slice(0, 3));
      } catch { /* warnings are optional */ }
    };
    const timer = setTimeout(checkWarnings, 2000);
    return () => clearTimeout(timer);
  }, [pageContext.path, enabled]);

  // ── Global keyboard shortcuts ──────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        const tag = document.activeElement?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
      if (e.key === 'Escape') setCommandPaletteOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // ── 5. Learning tracker ────────────────────────────────────
  const recordSuggestion = useCallback((type, accepted) => {
    setLearning(prev => {
      const next = { ...prev };
      if (!next[type]) next[type] = { accepted: 0, dismissed: 0 };
      if (accepted) next[type].accepted++; else next[type].dismissed++;
      saveLearning(next);
      return next;
    });
  }, []);

  const getSuggestionConfidence = useCallback((type) => {
    const stats = learning[type];
    if (!stats) return 1;
    const total = stats.accepted + stats.dismissed;
    if (total < 3) return 1;
    return stats.accepted / total;
  }, [learning]);

  // ── AI assist wrapper ──────────────────────────────────────
  const assist = useCallback(async (action, data = {}) => {
    if (!enabled) return null;
    try {
      // The server reads `entityData` — it destructures
      // { action, context, pageType, entityData, fieldName, currentValue, route }
      // and every prompt template interpolates entityData. Sending anything
      // under another name is silently dropped, so the published entity is
      // merged INTO entityData rather than sitting beside it.
      //
      // A call site's own entityData wins on conflict: it knows more about the
      // specific field it is asking about than the page does.
      // Background actions ask a generic question; the page's entity payload only
      // inflates the request. HiveLink publishes a ~10KB channel context, so
      // detect_warnings and detect_anomalies were carrying a transcript they
      // never use. Only person-triggered actions get it.
      const FOREGROUND = ['command', 'suggest_fields', 'generate_draft', 'summarize', 'ask'];
      const wantsContext = FOREGROUND.includes(action);
      const baseEntity = wantsContext ? (entityContext || {}) : {};
      const mergedEntity = (wantsContext && entityContext) || data.entityData
        ? { ...baseEntity, ...(data.entityData || {}) }
        : undefined;

      const res = await copilotAssist({
        action, pageType: pageContext.type,
        route: pageContext.path,
        ...data,
        ...(mergedEntity ? { entityData: mergedEntity } : {}),
        userHistory: pageHistory.slice(-3).map(p => p.label),
      }).unwrap();
      return res.data?.result || res.result || null;
    } catch { return null; }
  }, [enabled, copilotAssist, pageContext, pageHistory, entityContext]);

  // ── 9. Anomaly detection ───────────────────────────────────
  const checkAnomalies = useCallback(async (data) => {
    if (!enabled) return [];
    try {
      const result = await assist('detect_anomalies', { entityData: data });
      const parsed = JSON.parse((result || '{}').replace(/```json?\n?|```/g, ''));
      const anoms = parsed.anomalies || [];
      setAnomalies(anoms);
      return anoms;
    } catch { return []; }
  }, [assist, enabled]);

  const toggleEnabled = useCallback(() => {
    setEnabled(prev => {
      const next = !prev;
      localStorage.setItem('copilot_enabled', String(next));
      return next;
    });
  }, []);

  const dismissWarning = useCallback((idx) => {
    setWarnings(w => w.filter((_, i) => i !== idx));
    recordSuggestion('warning', false);
  }, [recordSuggestion]);

  // ── Task 2.11 — bind to the copilot the SERVER resolves for this route ──
  //
  // The provider previously rendered only the command palette, against the
  // generic assistant. Route-to-agent mapping lived in a hardcoded PAGE_MAP
  // here, so configuring a Co-Pilot agent changed nothing on screen. The
  // server now owns resolution, including tie-breaking, and returns which
  // surfaces the resolved copilot may use.
  const { data: resolved } = useResolveCopilotQuery(location.pathname, {
    skip: !enabled,
  });
  const boundAgent = resolved?.agent
    ? { ...resolved.agent, matchedPattern: resolved.matchedPattern, streamOptedOut: !resolved.stream?.enabled }
    : null;
  const surfaces = resolved?.surfaces || {};
  const recording = !!resolved?.stream?.enabled && !!resolved?.stream?.showIndicator;

  // ── Task 2.16 — emit real user actions, render whatever fires ──
  const [recordAction] = useRecordCopilotActionMutation();
  const [firedTriggers, setFiredTriggers] = useState([]);
  const lastFiredRef = useRef({});

  // ── Batched, fire-and-forget ────────────────────────────────────────
  //
  // This awaited a network POST per action. With the middleware emitting on
  // every query and mutation, each user action became: the real request, plus
  // a tracking POST, plus a database write, plus a 50-row trigger query. That
  // is what slowed the app down — super admin stayed fast because it does not
  // mount this provider.
  //
  // Actions now queue and flush together, off the interaction path entirely.
  const queueRef = useRef([]);
  const flushTimerRef = useRef(null);

  const flush = useCallback(() => {
    const batch = queueRef.current;
    queueRef.current = [];
    flushTimerRef.current = null;
    if (!batch.length || !boundAgent) return;

    // Not awaited. Nothing in the interface waits on telemetry.
    recordAction({
      agentId: boundAgent._id,
      route: location.pathname,
      actions: batch,
      lastFiredAt: lastFiredRef.current,
    }).unwrap().then((res) => {
      const fired = res?.triggers?.fired || [];
      if (fired.length) {
        // Copy before merging — RTK Query results are Immer-frozen.
        setFiredTriggers((prev) => [...prev, ...fired.filter((f) => !prev.some((p) => p.id === f.id))]);
        for (const f of fired) lastFiredRef.current[f.id] = new Date().toISOString();
      }
    }).catch(() => {
      // Best-effort. It must never break the page the user is on.
    });
  }, [boundAgent, location.pathname, recordAction]);

  const trackAction = useCallback((actionName, payload) => {
    if (!boundAgent) return;
    queueRef.current.push({ actionName, payload, at: new Date().toISOString() });

    // Flush when the batch is worth sending, otherwise shortly after the burst
    // settles. A page load fires several actions at once; one request covers
    // all of them.
    if (queueRef.current.length >= 10) { flush(); return; }
    if (!flushTimerRef.current) {
      flushTimerRef.current = setTimeout(flush, 4000);
    }
  }, [boundAgent, flush]);

  // Do not lose the tail of a batch when the person navigates or closes the tab.
  useEffect(() => {
    const onHide = () => { if (queueRef.current.length) flush(); };
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onHide);
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      if (queueRef.current.length) flush();
    };
  }, [flush]);

  // Hand the emitter to the RTK Query middleware, which sees every save and
  // failure in the app. Cleared when no copilot is bound so nothing is recorded
  // off a bound route.
  useEffect(() => {
    setCopilotEmitter(boundAgent && recording ? (name, payload) => trackAction(name, payload) : null);
    return () => setCopilotEmitter(null);
  }, [boundAgent, recording, trackAction]);

  const dismissTrigger = useCallback((id) => {
    setFiredTriggers((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── Emit the three actions the provider can observe on its own ──
  //
  // Without this, `trackAction` had no callers at all: nothing was ever
  // recorded, so every trigger rule was inert and the privacy controls
  // governed something that never happened. The picker also marked these
  // three "ready", which was untrue until now.
  //
  // Everything else in the catalogue still needs an explicit trackAction call
  // from the screen that knows the event happened — a copilot cannot detect
  // "saved successfully" from the outside.
  const seenRoutesRef = useRef(new Map());

  useEffect(() => {
    if (!boundAgent || !recording) return undefined;
    const route = location.pathname;
    const now = Date.now();
    const lastSeen = seenRoutesRef.current.get(route);

    // A return visit within ten minutes is the signal worth distinguishing —
    // it usually means the first attempt did not work.
    trackAction(lastSeen && now - lastSeen < 600000 ? 'page_revisit' : 'page_view', { route });
    seenRoutesRef.current.set(route, now);

    // Tab switches show up as a query param or hash change on the same path.
    // Treating them as a fresh page_view would drown the real signal.
    const tabKey = `${location.search}${location.hash}`;
    const prevTab = seenRoutesRef.current.get(`${route}#tab`);
    if (prevTab !== undefined && prevTab !== tabKey) {
      trackAction('tab_switch', { route, tab: tabKey });
    }
    seenRoutesRef.current.set(`${route}#tab`, tabKey);

    // Dwell: still here, untouched, after 30 seconds.
    let idle = true;
    const markActive = () => { idle = false; };
    window.addEventListener('keydown', markActive, { once: true });
    window.addEventListener('pointerdown', markActive, { once: true });

    const dwellTimer = setTimeout(() => {
      if (idle) trackAction('page_dwell', { route, seconds: 30 });
    }, 30000);

    return () => {
      clearTimeout(dwellTimer);
      window.removeEventListener('keydown', markActive);
      window.removeEventListener('pointerdown', markActive);
    };
  }, [boundAgent, recording, location.pathname, location.search, location.hash, trackAction]);

  // ── Sidebar conversation ──
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarMessages, setSidebarMessages] = useState([]);
  const [sidebarBusy, setSidebarBusy] = useState(false);

  const askSidebar = useCallback(async (text) => {
    setSidebarMessages((prev) => [...prev, { role: 'user', content: text }]);
    setSidebarBusy(true);
    try {
      const r = await assist('command', { context: text, route: location.pathname });
      setSidebarMessages((prev) => [...prev, {
        role: 'assistant',
        content: r?.result || 'I could not answer that.',
        blocked: !!r?.blocked,
      }]);
    } catch (err) {
      setSidebarMessages((prev) => [...prev, { role: 'assistant', content: `Something went wrong: ${err.message}` }]);
    } finally {
      setSidebarBusy(false);
    }
  }, [assist, location.pathname]);

  /**
   * Memoised. This was a plain object literal, so every provider render created
   * a new context value and re-rendered every consumer. That was tolerable when
   * the provider only wrapped MainLayout pages; it is not now that it wraps the
   * whole standalone HiveLink route, where a copilot state tick re-rendered the
   * channel list, the message list and every open panel.
   */
  const value = useMemo(() => ({
    enabled, toggleEnabled,
    boundAgent, surfaces, recording,
    // Surface flags the provider cannot render itself: the palette lives in
    // MainLayout, and inline suggestions belong beside the form field that
    // needs them. Exposing them here is what makes those settings mean
    // something rather than sit unread in the database.
    paletteEnabled: surfaces.palette !== false,
    inlineEnabled: surfaces.inline !== false,
    // Behaviour flags, read by whichever surface acts on them.
    suggestActions: resolved?.behaviour?.suggestActions !== false,
    autoFillForms: resolved?.behaviour?.autoFillForms === true,
    inlineGeneration: resolved?.behaviour?.inlineGeneration !== false,
    contextDepth: resolved?.behaviour?.contextDepth || 'page',
    trackAction, firedTriggers, dismissTrigger,
    openSidebar: () => setSidebarOpen(true),
    pageContext, pageHistory,
    entityContext, publishEntity,
    warnings, dismissWarning,
    anomalies, checkAnomalies,
    commandPaletteOpen, setCommandPaletteOpen,
    assist, recordSuggestion, getSuggestionConfidence,
  }), [
    enabled, toggleEnabled, boundAgent, surfaces, recording, resolved,
    trackAction, firedTriggers, dismissTrigger, pageContext, pageHistory,
    entityContext, publishEntity, warnings, dismissWarning, anomalies,
    checkAnomalies, commandPaletteOpen, assist, recordSuggestion,
    getSuggestionConfidence,
  ]);;

  // Only the surfaces the resolved copilot DECLARES are rendered. A copilot
  // that has not enabled `toast` cannot interrupt, whatever a rule says.
  const toastTrigger = surfaces.toast ? firedTriggers.find((t) => t.surface === 'toast') : null;

  return (
    <CopilotContext.Provider value={value}>
      {children}

      {enabled && boundAgent && surfaces.orb !== false && (
        <CopilotOrb
          agent={boundAgent}
          recording={recording}
          pulse={firedTriggers.some((t) => t.surface === 'orb')}
          onOpenSidebar={() => setSidebarOpen(true)}
          onOpenPalette={() => setCommandPaletteOpen(true)}
        />
      )}

      {enabled && boundAgent && surfaces.sidebar !== false && (
        <CopilotSidebar
          agent={boundAgent}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onAsk={askSidebar}
          messages={sidebarMessages}
          busy={sidebarBusy}
          recording={recording}
          firedTriggers={firedTriggers.filter((t) => t.surface === 'sidebar')}
          onDismissTrigger={dismissTrigger}
          onOpenPrivacy={openPrivacySettings}
        />
      )}

      {enabled && boundAgent && toastTrigger && (
        <CopilotToast
          trigger={toastTrigger}
          agentName={boundAgent.name}
          onDismiss={dismissTrigger}
          onAccept={(t) => { dismissTrigger(t.id); setSidebarOpen(true); askSidebar(t.prompt || t.label); }}
        />
      )}
    </CopilotContext.Provider>
  );
}